/**
 * Milestone gates (Jane's partner-form reorg, 2026-07-16).
 *
 * The three 47SP applicant forms group their sections into four milestones:
 * 1 Application, 2 Lodgement, 3 Form 80, 4 Relationship evidence. Gates are a
 * grouping/progress concept ONLY: every section stays stage 1, visible and
 * editable from day one (the application is lodged complete per the
 * Tzvi + Eli single-stage ruling). Gates drive the section-nav grouping, the
 * per-milestone progress badges, and what ADI chases first.
 */

import type { SectionDef } from '../schema-renderer/types';
import type { SectionProgress } from './validateForm';

export const GATE_LABELS: Record<number, string> = {
  1: 'Application',
  2: 'Lodgement',
  3: 'Form 80',
  4: 'Relationship evidence',
};

export interface GateGroup {
  gate: number;
  label: string;
  /** Indices into the (stage-filtered, sorted) sections array. */
  sectionIndices: number[];
}

/**
 * Group an ordered section list into contiguous gate groups. Returns [] when
 * no section carries a gate (non-gated forms render the flat nav unchanged).
 * Sections without a gate on a gated form inherit the previous section's gate
 * (defensive; the partner schemas tag every section).
 */
export function groupSectionsByGate(sections: SectionDef[]): GateGroup[] {
  if (!sections.some((s) => s.gate != null)) return [];
  const groups: GateGroup[] = [];
  let current: GateGroup | null = null;
  let lastGate = 1;
  sections.forEach((s, i) => {
    const gate = s.gate ?? lastGate;
    lastGate = gate;
    if (!current || current.gate !== gate) {
      current = { gate, label: GATE_LABELS[gate] ?? `Milestone ${gate}`, sectionIndices: [] };
      groups.push(current);
    }
    current.sectionIndices.push(i);
  });
  return groups;
}

/**
 * Aggregate per-section progress into a gate percentage over required fields.
 * `excludeIndices` removes context-only sections (all-foreign, read-only
 * cross-form prefill) so they never distort the client's milestone badge.
 * A gate with no required fields reads 100 once any of its sections is
 * touched, else 0 (mirrors the empty-form convention in computeOwnFieldProgress).
 */
export function gateProgressPct(
  group: GateGroup,
  sectionProgress: SectionProgress[],
  excludeIndices: Set<number> = new Set(),
): number {
  let total = 0;
  let missing = 0;
  let touched = false;
  for (const i of group.sectionIndices) {
    if (excludeIndices.has(i)) continue;
    const p = sectionProgress[i];
    if (!p) continue;
    total += p.totalRequired;
    missing += p.missingRequired;
    if (p.touched) touched = true;
  }
  if (total === 0) return touched ? 100 : 0;
  return Math.round(((total - missing) / total) * 100);
}
